import { useRef, useState } from "react";
import { MessageSquare, Paperclip, Send, FileText } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function RegCommentThread({ r, onChanged }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const comments = r.comments || [];
  const send = async () => {
    const f = fileRef.current?.files?.[0];
    if (!text.trim() && !f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("body", text.trim());
      if (f) fd.append("file", f);
      const { data } = await api.post(`/registrations/${r.id}/comments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); if (fileRef.current) fileRef.current.value = "";
      onChanged(data); toast.success("Message sent");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3" data-testid={`reg-comments-${r.id}`}>
      <button type="button" onClick={() => setOpen(o => !o)} className="text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100 inline-flex items-center gap-1" data-testid={`reg-comments-toggle-${r.id}`}>
        <MessageSquare className="w-3 h-3" /> Messages{comments.length ? ` (${comments.length})` : ""}
      </button>
      {open && (
        <div className="mt-2 rounded border border-border p-3 space-y-2">
          {comments.length === 0 && <div className="text-xs opacity-50">No messages yet — ask a question or add extra information here.</div>}
          {comments.map(c => (
            <div key={c.id} className={`text-xs rounded px-3 py-2 ${c.is_manager ? "bg-[#83B68B]/10 border border-[#83B68B]/30" : "bg-muted"}`} data-testid={`reg-comment-${c.id}`}>
              <div className="opacity-60 text-[10px]">{c.by_name}{c.is_manager ? " · Registrations team" : ""} · {format(parseISO(c.at), "d MMM HH:mm")}</div>
              {c.body && <div className="mt-0.5 whitespace-pre-wrap">{c.body}</div>}
              {c.attachment && <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${c.attachment.path}`} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-[#83B68B] underline"><FileText className="w-3 h-3" /> {c.attachment.name}</a>}
            </div>
          ))}
          <div className="flex items-end gap-2 pt-1">
            <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder="Add information or ask a question…" className="flex-1 bg-transparent border border-border rounded px-2 py-1.5 text-xs outline-none focus:border-[#83B68B]" data-testid={`reg-comment-input-${r.id}`} />
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" id={`reg-cfile-${r.id}`} data-testid={`reg-comment-file-${r.id}`} />
            <label htmlFor={`reg-cfile-${r.id}`} className="p-2 rounded border border-border hover:border-[#83B68B] cursor-pointer" title="Attach photo or PDF"><Paperclip className="w-3.5 h-3.5" /></label>
            <button type="button" disabled={busy} onClick={send} className="btn-volt p-2 rounded disabled:opacity-40" data-testid={`reg-comment-send-${r.id}`}><Send className="w-3.5 h-3.5" /></button>
          </div>
        </div>
      )}
    </div>
  );
}

export function StageSetter({ stages, labels, current, onSet, testId }) {
  const [v, setV] = useState("");
  return (
    <div className="inline-flex items-center gap-1" data-testid={testId}>
      <select value={v} onChange={e => setV(e.target.value)} className="bg-transparent border border-border rounded px-2 py-1 text-[10px] tracking-widest uppercase outline-none focus:border-[#83B68B]" data-testid={`${testId}-select`}>
        <option value="">Set stage…</option>
        {stages.map(s => <option key={s} value={s} disabled={s === current} className="bg-background">{labels[s]}{s === current ? " (current)" : ""}</option>)}
      </select>
      <button type="button" disabled={!v} onClick={() => { const note = window.prompt("Note for the coach (optional):") ?? null; onSet(v, note); setV(""); }} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] disabled:opacity-30" data-testid={`${testId}-apply`}>Apply</button>
    </div>
  );
}
